/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ArrowUpTrayIcon, PencilSquareIcon, ChartBarIcon, BanknotesIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { TableCellsIcon, CogIcon, ArrowDownOnSquareIcon, ArrowsUpDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useSelector } from 'react-redux';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import ToolTipNoIcon from '../../elements/ToolTipNoIcon';
import Button from '../../elements/Button';
import { SpinnerRight } from '../../assets/mdiIcons';

// if only one button - do not render
function Buttons({
  modeObjectsToShow,
  currentMode,
  setCurrentMode,
  topLevelFormatting,
  activeTab,
  actionButtons,
  handleSave,
  handleSync,
  accountSyncStatus,
  handlePrevNext,
  handleCancel,
  transformationParams,
  setTransformationParams,
  handleUndoTransform,
  showTableFilters,
  activeTableFilterCount,
  onOpenTableFilters,
  isMobile,
}) {
  Buttons.propTypes = {
    modeObjectsToShow: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
    setCurrentMode: PropTypes.func,
    currentMode: PropTypes.string,
    topLevelFormatting: PropTypes.string,
    activeTab: PropTypes.string.isRequired,
    actionButtons: PropTypes.arrayOf(PropTypes.string),
    handleSave: PropTypes.func,
    handleSync: PropTypes.func,
    handleCancel: PropTypes.func,
    accountSyncStatus: PropTypes.bool.isRequired,
    handlePrevNext: PropTypes.func,
    transformationParams: PropTypes.arrayOf(PropTypes.string),
    setTransformationParams: PropTypes.func,
    handleUndoTransform: PropTypes.func,
    showTableFilters: PropTypes.bool,
    activeTableFilterCount: PropTypes.number,
    onOpenTableFilters: PropTypes.func,
    isMobile: PropTypes.bool,
  };
  Buttons.defaultProps = {
    modeObjectsToShow: [{ id: 'table' }, { id: 'grid' }],
    currentMode: 'table',
    setCurrentMode: () => {},
    topLevelFormatting: '',
    actionButtons: [],
    handleSave: () => {},
    handlePrevNext: () => {},
    handleSync: () => {},
    handleCancel: () => {},
    transformationParams: [],
    setTransformationParams: () => {},
    handleUndoTransform: () => {},
    showTableFilters: false,
    activeTableFilterCount: 0,
    onOpenTableFilters: () => {},
    isMobile: false,
  };

  const { t } = useTranslation('accountDetails', { keyPrefix: 'buttons' });

  const dashboardModeSelect = useSelector((state) => state.simulation.dashboardMode);
  const dashboardMode = React.useMemo(() => dashboardModeSelect, [dashboardModeSelect]);

  // metals - historical value calculation
  const [getPurchaseValueLoading, setGetPurchaseValueLoading] = React.useState(false); // controls the loading state of the historical value calculation for metals

  function handleGridGetPurchaseValuesCompleted(event) {
    setGetPurchaseValueLoading(false);
    event.target.removeEventListener('gridGetPurchaseValues', handleGridGetPurchaseValuesCompleted);
  }

  // handle calculation of historical value for metals
  function handleHistoricalValueCalc() {
    setGetPurchaseValueLoading(true);
    window.dispatchEvent(new CustomEvent('gridGetPurchaseValues')); // this event notifies Grid that it should calculate historical values

    // when Grid is done, it sends this event
    window.addEventListener('handleGridGetPurchaseValuesCompleted', handleGridGetPurchaseValuesCompleted);
  }

  const mobileIconButtonClassName = 'sm:hidden inline-flex h-11 w-11 min-w-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brandBlue-200 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div
      id="rightButtons"
      className={`text-sm ${topLevelFormatting} ${isMobile ? 'w-full flex items-start justify-between gap-2' : 'flex justify-center items-center space-x-2'}`}
    >
      <div className={`flex items-center gap-2 ${isMobile ? 'flex-wrap' : ''}`}>
        {/*
        SYNC
      */}
        {actionButtons.includes('sync') && (
        <ToolTipNoIcon info={t('sync.tooltip')}>
          <>
            <button type="button" onClick={handleSync} className={mobileIconButtonClassName} disabled={accountSyncStatus} aria-label="Sync">
              <SpinnerRight className={`h-5 w-5 ${accountSyncStatus ? 'animate-spin' : ''}`} />
            </button>
            <Button text="Sync" Icon={SpinnerRight} onClick={handleSync} spinnerOn={accountSyncStatus} formatting="hidden sm:flex w-24 justify-center" />
          </>
        </ToolTipNoIcon>
        )}

        {showTableFilters && (
        <ToolTipNoIcon info={t('tableFilters.tooltip')}>
          <>
            <button
              type="button"
              onClick={onOpenTableFilters}
              className={`${mobileIconButtonClassName} relative ${activeTableFilterCount > 0 ? 'bg-brandBlue-50 text-brandBlue-700 border-brandBlue-200' : ''}`}
              aria-label={t('tableFilters.label')}
            >
              <FunnelIcon className="h-5 w-5" />
              {activeTableFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brandBlue-600 px-1 text-[10px] font-bold text-white">
                  {activeTableFilterCount}
                </span>
              )}
            </button>
            <Button
              text={t('tableFilters.label')}
              onClick={onOpenTableFilters}
              Icon={FunnelIcon}
              withAccent={activeTableFilterCount > 0}
              formatting="hidden sm:flex w-24 justify-center"
            />
          </>
        </ToolTipNoIcon>
        )}

        {actionButtons.includes('historicalValueCalc') && (
        <ToolTipNoIcon info={t('historicalValueCalc.tooltip')}>
          <Button text={t('historicalValueCalc.label')} onClick={handleHistoricalValueCalc} noFrame formatting="mt-0.5" spinnerOn={getPurchaseValueLoading} />
        </ToolTipNoIcon>
        )}

        {/* DECIMAL POINT SWITCH */}

        {actionButtons.includes('decimalChar') && (
        <span className="relative inline-flex">
          <ToolTipNoIcon info={t('decimalChar.tooltip')}>
            <span className="mr-2 ">{t('decimalChar.label')}</span>
            <button
              type="button"
              id="account-details-grid-decimal-button"
              onClick={() => {
                const newDecimalChar = transformationParams.decimalChar === ',' ? '.' : ',';
                setTransformationParams((prev) => ({ ...prev, decimalChar: newDecimalChar }));
              }}
              className="rounded-md inline-flex items-center px-1.5 xs:px-4 py-1.5 border font-bold text-sm focus:z-10 focus:outline-none focus:ring-1 focus:ring-brandBlue-400 focus:border-brandBlue-400 border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
            >
              {transformationParams.decimalChar}
            </button>
          </ToolTipNoIcon>
        </span>
        )}

        {/* HEADERS IN ROW */}

        {actionButtons.includes('headersInRow') && (
        <span className="relative inline-flex">
          <ToolTipNoIcon info={t('headersInRow.tooltip')}>
            <span className="mr-2 ">{t('headersInRow.label')}</span>
            <input
              type="number"
              min="0"
              step="1"
              defaultValue={transformationParams?.headersInRow ? transformationParams.headersInRow + 1 : ''}
              id="account-details-grid-headersinrow-button"
              onChange={(e) => {
                if (Number.isNaN(Number(e.target.value)) || Number(e.target.value) < 0) setTransformationParams((prev) => ({ ...prev, headersInRow: null }));
                setTransformationParams((prev) => ({ ...prev, headersInRow: Number(e.target.value) - 1 }));
              }}
              className="inline-flex items-center w-16 px-1.5 py-1.5 text-center border border-gray-300 bg-white font-medium text-sm rounded-md text-gray-700 focus:outline-none focus:ring-2
                            focus:ring-offset-2 focus:ring-brandBlue-400"
            />
          </ToolTipNoIcon>
        </span>
        )}

        {/* INFO SAVE (PREVIEWER) */}

        {actionButtons.includes('infoSave') && (
        <div className="flex items-center rounded-md bg-brandBlue-50 px-3 py-1" id="info-message-save">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-brandBlue-400" aria-hidden="true" />
          </div>
          <div className="ml-3 flex-1 md:flex md:justify-between">
            <p className="text-sm text-brandBlue-600">{t('editYourFile')}</p>
          </div>
        </div>
        )}

        {/* INFO TRANSFORMED (PREVIEWER) */}

        {actionButtons.includes('infoTransformed') && (
        <div className="rounded-md bg-brandBlue-50 px-3 py-1 flex items-center" id="info-message-transformed">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-brandBlue-400" aria-hidden="true" />
          </div>
          <div className="ml-3 flex-1 md:flex md:justify-between md:items-center">
            <p className="text-sm text-brandBlue-600">{t('fileTransformed')}</p>
            <Button text={t('fileTransformedUndo')} onClick={handleUndoTransform} noFrame size="xs" formatting="ml-2" textColor="text-brandBlue-700" textColorHover="text-brandBlue-800" />
          </div>
        </div>
        )}

        {/* INFO READ ONLY IN PROJECT MODE */}

        {dashboardMode === 'projects' && (
        <ToolTipNoIcon info={t('readOnly.tooltip')} classNameOwn="h-full flex items-center">
          <span className="ml-2 px-1.5 rounded-full bg-brandBlue-300 border-[1px] border-brandBlue-300 text-white text-xs font-bold">i</span>
          <p className="ml-2 text-sm text-gray-500">{t('readOnly.label')}</p>
        </ToolTipNoIcon>
        )}

        {/* SAVE + CANCEL for Grid */}

        {(currentMode === 'grid' || currentMode === 'previewer') && actionButtons.includes('save') && actionButtons.includes('cancel') && (
        <div className="flex space-x-2 items-center mr-2">
          <ToolTipNoIcon info={t('save.tooltip')}>
            <Button text={t('save.label')} withAccent onClick={handleSave} />
          </ToolTipNoIcon>

          <ToolTipNoIcon info={t('cancel.tooltip')}>
            <Button text={t('cancel.label')} onClick={handleCancel} />
          </ToolTipNoIcon>
        </div>
        )}
      </div>
      {/*
        MODE SELECT BUTTONS --> hide when settings are displayed, because they only have one mode
      */}
      {activeTab !== 'settings' && (modeObjectsToShow || []).length > 1 && (
        <span
          id="modeSelectButtons"
          className={`inline-flex items-center gap-1 rounded-full bg-gray-100 px-1 py-1 shadow-xs ring-1 ring-inset ring-gray-200 ${isMobile ? 'ml-auto shrink-0' : ''}`}
        >
          {modeObjectsToShow.map((modeObject) => (
            <ToolTipNoIcon info={t(`${modeObject.id}.tooltip`)}>
              <button
                key={modeObject.id}
                type="button"
                onClick={() => setCurrentMode(modeObject.id)}
                className={`${
                  modeObject.id === currentMode ? 'bg-white text-gray-900 hover:shadow shadow-sm' : 'text-gray-700 hover:text-gray-900 hover:bg-white'
                } relative inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold transition hover:shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brandBlue-500`}
              >
                {t(`${modeObject.id}.label`)}
              </button>
            </ToolTipNoIcon>
          ))}
        </span>
      )}
    </div>
  );
}

export default function ButtonBar({
  buttonsToShow,
  displayedData,
  displayedComponent,
  handleClick,
  handleSync,
  accountSyncStatus,
  handleSave,
  handlePrevNext,
  afterCancelGoTo,
  transformationParams,
  setTransformationParams,
  handleUndoTransform,
  hideBottomBorder,
  showTableFilters,
  activeTableFilterCount,
  onOpenTableFilters,
}) {
  ButtonBar.propTypes = {
    buttonsToShow: PropTypes.arrayOf(PropTypes.string).isRequired,
    displayedComponent: PropTypes.string.isRequired,
    displayedData: PropTypes.string.isRequired,
    handleClick: PropTypes.func.isRequired,
    handleSync: PropTypes.func,
    accountSyncStatus: PropTypes.bool,
    handleSave: PropTypes.func,
    userChangesPresent: PropTypes.bool.isRequired,
    handlePrevNext: PropTypes.func,
    afterCancelGoTo: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    transformationParams: PropTypes.object.isRequired,
    setTransformationParams: PropTypes.func.isRequired,
    handleUndoTransform: PropTypes.func,
    hideBottomBorder: PropTypes.bool,
    showTableFilters: PropTypes.bool,
    activeTableFilterCount: PropTypes.number,
    onOpenTableFilters: PropTypes.func,
  };
  ButtonBar.defaultProps = {
    handleSync: () => {},
    accountSyncStatus: false,
    handleSave: () => {},
    handlePrevNext: () => {},
    afterCancelGoTo: 'table',
    handleUndoTransform: () => {},
    hideBottomBorder: false,
    showTableFilters: false,
    activeTableFilterCount: 0,
    onOpenTableFilters: () => {},
  };

  const { t } = useTranslation('accountDetails', { keyPrefix: 'buttons' });

  const dashboardModeSelect = useSelector((state) => state.simulation.dashboardMode);

  // FIXME
  const [getPurchaseValueLoading, setGetPurchaseValueLoading] = useState(false); // controls the loading state of the historical value calculation for metals

  // TODO go over old buttons bar, there may be more modes / tabs
  // intialise tabs here (get only names for possible tabs from buttonsToShow + settings)
  const tabs = buttonsToShow.filter((b) => ['holdings', 'cashflow', 'quotes', 'settings'].includes(b));
  // this manages active tab -- initialised to whatever position 'holdings' it (or 0)
  const activeTab = tabs.find((x) => x === displayedData);
  // console.log('activeTab', activeTab, displayedData, buttonsToShow, tabs);

  // intialise modes here (get only names for possible modes from buttonsToShow)
  const modes = buttonsToShow.filter((b) => ['table', 'grid', 'import', 'wizard'].includes(b));
  // this manages active mode -- intialised to whatever position 'table' is
  const [currentMode, setCurrentMode] = useState(modes.find((x) => x === 'table') ? 'table' : modes[0]);

  // initialise action buttons
  const actionButtons = buttonsToShow.filter((b) => ['save', 'cancel', 'sync', 'decimalChar', 'headersInRow', 'infoSave', 'infoTransformed', 'historicalValueCalc'].includes(b));
  // add buttons for specific combinations
  if (displayedComponent === 'wizard') actionButtons.push('prevNext');

  const definitions = {
    holdings: { icon: BanknotesIcon }, // the i18n also has a .tooltip
    cashflows: { icon: ArrowsUpDownIcon },
    quotes: { icon: ChartBarIcon },
    settings: { icon: CogIcon },
    table: { icon: TableCellsIcon },
    grid: { icon: TableCellsIcon },
    import: { icon: ArrowUpTrayIcon },
    save: { icon: PencilSquareIcon }, // TO BE IMPLEMENTED
    sync: { icon: ArrowDownOnSquareIcon }, // TO BE IMPLEMENTED
    wizard: { icon: PlusIcon }, // "Add / Edit" for unlistedShares
  };

  const tabObjectsToShow = tabs.map((tab) => ({ id: tab, icon: (definitions[tab] || {}).icon || TableCellsIcon, ...definitions[tab] }));

  const modeObjectsUnsorted = modes.map((modeItem) => ({ id: modeItem, ...definitions[modeItem] }));
  const modeObjectsSorted = [];

  modeObjectsUnsorted.forEach((modeObj) => {
    if (modeObj.id === 'table') modeObjectsSorted.unshift(modeObj);
    else if (modeObj.id === 'grid') modeObjectsSorted.splice(1, 0, modeObj);
    else modeObjectsSorted.push(modeObj);
  });
  // console.log('DEBUG', modeObjectsUnsorted, modeObjectsSorted);

  function handleGridGetPurchaseValuesCompleted(event) {
    setGetPurchaseValueLoading(false);
    event.target.removeEventListener('gridGetPurchaseValues', handleGridGetPurchaseValuesCompleted);
  }

  // FIXME handle calculation of historical value for metals
  function handleHistoricalValueCalc() {
    setGetPurchaseValueLoading(true);
    window.dispatchEvent(new CustomEvent('gridGetPurchaseValues')); // this event notifies Grid that it should calculate historical values

    // when Grid is done, it sends this event
    window.addEventListener('handleGridGetPurchaseValuesCompleted', handleGridGetPurchaseValuesCompleted);
  }

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
  }

  function handleCancel() {
    handleClick(afterCancelGoTo || 'table'); // TODO not sure if table is a good fallback in all use cases
  }

  // TODO orchestrate button actions as in the previous version

  const buttonsProps = {
    activeTab,
    currentMode: displayedComponent,
    modeObjectsToShow: modeObjectsSorted,
    actionButtons,
    setCurrentMode: (mode) => {
      setCurrentMode(mode);
      handleClick(mode);
    },
    handleSync,
    accountSyncStatus,
    handleSave,
    handlePrevNext,
    handleCancel,
    transformationParams,
    setTransformationParams,
    handleUndoTransform,
    showTableFilters,
    activeTableFilterCount,
    onOpenTableFilters,
  };

  return (
    <div className="w-full mb-4">
      <div className="grid grid-cols-1 sm:hidden items-center" id="tabsMobile">
        {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
        <div className="col-start-1 row-start-1 relative">
          <select
            value={activeTab}
            aria-label="Select a tab"
            className="w-full appearance-none rounded-lg bg-white py-2 pr-10 pl-3 text-base text-gray-900 border border-gray-400 shadow-sm focus:border-brandBlue-300 focus:ring-2 focus:ring-brandBlue-100 focus:outline-none"
            onChange={(event) => handleClick(event.target.value)}
          >
            {tabObjectsToShow.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {t(`${tab.id}.label`)}
              </option>
            ))}
          </select>
          <ChevronDownIcon aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-3 my-auto h-4 w-4 fill-gray-500" />
        </div>
        <Buttons topLevelFormatting="pt-4" isMobile {...buttonsProps} />
      </div>
      <div className="hidden sm:block" id="tabsDesktop">
        <div className={`${hideBottomBorder ? '' : 'border-b border-gray-200 dark:border-white/10'} flex items-center justify-between`}>
          <nav aria-label="Tabs" className="-mb-px flex space-x-8">
            {tabObjectsToShow.map((tab) => (
              <ToolTipNoIcon info={t(`${tab.id}.tooltip`)}>
                <button
                  key={tab.id}
                  type="button"
                  aria-current={tab.id === activeTab ? 'page' : undefined}
                  onClick={() => {
                    handleClick(tab.id);
                  }}
                  className={classNames(
                    tab.id === activeTab ? 'border-brandBlue-500 text-brandBlue-600' : 'border-transparent text-gray-700 hover:border-gray-300 hover:text-gray-900 ',
                    'group inline-flex items-center border-b-2 px-1 py-4 text-sm font-semibold',
                  )}
                >
                  <tab.icon aria-hidden="true" className={classNames(tab.id === activeTab ? 'text-brandBlue-500' : 'text-gray-700 group-hover:text-gray-900', 'hidden lg:block mr-2 -ml-0.5 h-5')} />
                  <span>{t(`${tab.id}.label`)}</span>
                </button>
              </ToolTipNoIcon>
            ))}
          </nav>
          <Buttons topLevelFormatting="ml-4" {...buttonsProps} />
        </div>
      </div>
    </div>
  );
}
